import { memo, useEffect, useState } from 'react'
import { getWaveValuesFromAudioBuffer } from '../../utils/get-wave-values-from-audio-buffer.ts'

interface AudioWaveProps {
  width: number
  height: number
  audioBuffer?: AudioBuffer
  color?: string
  spacing?: number
  lineWidth?: number
  startOffset?: number
  endOffset?: number
}

const AudioWave = ({
  width,
  height,
  audioBuffer,
  color = '#FFFFFF77',
  spacing = 2,
  lineWidth = 2,
  startOffset = 0,
  endOffset = 0,
}: AudioWaveProps) => {
  const [audioSamples, setAudioSamples] = useState<number[]>([])
  const sampleWidth = lineWidth + spacing

  useEffect(() => {
    if (audioBuffer) {
      setAudioSamples(
        getWaveValuesFromAudioBuffer(audioBuffer, width / sampleWidth)
      )
    }
  }, [audioBuffer, width, sampleWidth])

  const renderAudioWave = (): JSX.Element => {
    const layers = [color]
    const visibleStart = Math.ceil(startOffset / sampleWidth)
    const visibleEnd = Math.floor((width - endOffset) / sampleWidth)

    return (
      <svg width={width} height={height}>
        {layers.map((color) =>
          audioSamples.map((volume, index) => {
            if (index < visibleStart || index > visibleEnd) return null

            const lineHeight = volume * height - lineWidth * 2
            const x = index * sampleWidth + 1
            const y = (height - lineHeight) / 2

            return (
              <line
                key={index}
                x1={x}
                x2={x}
                y1={y}
                y2={y + lineHeight}
                stroke={color}
                strokeWidth={lineWidth}
                strokeLinecap="square"
              />
            )
          })
        )}
      </svg>
    )
  }

  return (
    <div
      className="w-full"
      style={{ height: height, width: width, overflow: 'hidden' }}
    >
      {audioSamples.length > 0 ? renderAudioWave() : null}
    </div>
  )
}

export default memo(AudioWave)
